<div class="scheduler-report-dialog">
    <!-- Header -->
    <div class="section-header no-print" mat-dialog-title>
        <mat-icon class="section-icon">assessment</mat-icon>
        <span class="section-title">{{ 'ai.schedulerForecast' | transloco }}</span>
        <button mat-icon-button class="close-button" mat-dialog-close>
            <mat-icon>close</mat-icon>
        </button>
    </div>
    <!-- Time Range Selector -->
    <div class="time-range-selector no-print">
        <div class="range-label">
            <mat-icon>date_range</mat-icon>
            <span>{{ 'ai.selectTimeRange' | transloco }}</span>
        </div>
        <div class="range-buttons">
            <button mat-stroked-button
                    [class.selected]="selectedTimeRange === '7'"
                    (click)="onTimeRangeChange('7')">
                {{ 'ai.oneWeek' | transloco }}
            </button>
            <button mat-stroked-button
                    [class.selected]="selectedTimeRange === '30'"
                    (click)="onTimeRangeChange('30')">
                {{ 'ai.oneMonth' | transloco }}
            </button>
            <button mat-stroked-button
                    [class.selected]="selectedTimeRange === '90'"
                    (click)="onTimeRangeChange('90')">
                {{ 'ai.threeMonths' | transloco }}
            </button>
            <button mat-stroked-button
                    [class.selected]="selectedTimeRange === 'custom'"
                    (click)="onTimeRangeChange('custom')">
                {{ 'ai.customDays' | transloco }}
            </button>
        </div>
        @if (selectedTimeRange === 'custom') {
            <div class="custom-days-selector">
                <app-drop-number-selector
                    [value]="customDays"
                    [min]="1"
                    [max]="365"
                    [step]="1"
                    [label]="'ai.days' | transloco"
                    [showManualInput]="true"
                    (onChange)="onCustomDaysChange($event)">
                </app-drop-number-selector>
            </div>
        }
    </div>

    <!-- Main Content -->
    <div mat-dialog-content class="dialog-content">
        @if (loading) {
            <div class="loading-container">
                <mat-spinner diameter="50"></mat-spinner>
                <p>{{ 'ai.generatingReport' | transloco }}</p>
            </div>
        } @else if (reportData) {
            <div class="report-document">
                <!-- Document Header -->
                <div class="document-header">
                    <h1 class="document-title">SCHEDULER CONTENT FORECAST</h1>
                    <div class="document-info">
                        <div class="info-row">
                            <span class="label">Schedule Name:</span>
                            <span class="value">{{ reportData.schedule.schedule_name }}</span>
                        </div>
                        @if (reportData.schedule.description) {
                            <div class="info-row">
                                <span class="label">Description:</span>
                                <span class="value">{{ reportData.schedule.description }}</span>
                            </div>
                        }
                        <div class="info-row">
                            <span class="label">Status:</span>
                            <span class="value">{{ reportData.schedule.active ? 'ACTIVE' : 'INACTIVE' }}</span>
                        </div>
                        <div class="info-row">
                            <span class="label">Report Generated:</span>
                            <span class="value">{{ getCurrentDateTime() }}</span>
                        </div>
                        <div class="info-row">
                            <span class="label">Forecast Period:</span>
                            <span class="value">{{ getForecastPeriod() }}</span>
                        </div>
                    </div>
                </div>

                <div class="document-divider"></div>

                <!-- Executive Summary -->
                <div class="document-section">
                    <h2 class="section-heading">EXECUTIVE SUMMARY</h2>
                    <table class="summary-table">
                        <tr>
                            <td class="summary-label">Total Events Configured:</td>
                            <td class="summary-value">{{ reportData.statistics.totalEvents }}</td>
                        </tr>
                        <tr>
                            <td class="summary-label">Active Events:</td>
                            <td class="summary-value">{{ reportData.statistics.activeEvents }}</td>
                        </tr>
                        <tr>
                            <td class="summary-label">Inactive Events:</td>
                            <td class="summary-value">{{ reportData.statistics.inactiveEvents }}</td>
                        </tr>
                        <tr>
                            <td class="summary-label">Scenes Referenced:</td>
                            <td class="summary-value">{{ reportData.statistics.sceneCount }}</td>
                        </tr>
                        <tr>
                            <td class="summary-label">Resources Referenced:</td>
                            <td class="summary-value">{{ reportData.statistics.resourceCount }}</td>
                        </tr>
                        <tr>
                            <td class="summary-label">Daily Content Duration:</td>
                            <td class="summary-value">{{ schedulerReportService.formatDuration(reportData.statistics.totalDailyDuration) }}</td>
                        </tr>
                        <tr>
                            <td class="summary-label">Average Event Duration:</td>
                            <td class="summary-value">{{ schedulerReportService.formatDuration(reportData.statistics.averageEventDuration) }}</td>
                        </tr>
                        <tr>
                            <td class="summary-label">Operating Hours:</td>
                            <td class="summary-value">{{ reportData.statistics.earliestTime }}
                                - {{ reportData.statistics.latestTime }}
                            </td>
                        </tr>
                        <tr>
                            <td class="summary-label">Daily Utilization:</td>
                            <td class="summary-value">{{ reportData.statistics.utilizationPercentage }}%</td>
                        </tr>
                        <tr>
                            <td class="summary-label">Total Scheduled Occurrences ({{ daysToExpand }} days):</td>
                            <td class="summary-value">{{ reportData.expandedOccurrences.length }}</td>
                        </tr>
                    </table>
                </div>

                <div class="document-divider"></div>

                <!-- Visual Analytics Section (Charts) -->
                @if (chartsReady) {
                    <div class="document-section charts-section">
                        <h2 class="section-heading">VISUAL ANALYTICS</h2>
                        <p class="section-description">Interactive charts and graphs showing schedule patterns and
                            trends:</p>

                        <div class="charts-grid">
                            <!-- Chart 1: Recurrence Patterns -->
                            <div class="chart-container">
                                <highcharts-chart
                                    [callbackFunction]="chartCallback"
                                    [Highcharts]="Highcharts"
                                    [options]="recurrenceChart">
                                </highcharts-chart>
                            </div>

                            <!-- Chart 2: Content Usage -->
                            <div class="chart-container">
                                <highcharts-chart
                                    [callbackFunction]="chartCallback"
                                    [Highcharts]="Highcharts"
                                    [options]="contentUsageChart">
                                </highcharts-chart>
                            </div>

                            <!-- Chart 3: Daily Distribution -->
                            <div class="chart-container chart-wide">
                                <highcharts-chart
                                    [callbackFunction]="chartCallback"
                                    [Highcharts]="Highcharts"
                                    [options]="dailyDistributionChart">
                                </highcharts-chart>
                            </div>

                            <!-- Chart 4: Hourly Distribution -->
                            <div class="chart-container">
                                <highcharts-chart
                                    [callbackFunction]="chartCallback"
                                    [Highcharts]="Highcharts"
                                    [options]="hourlyDistributionChart">
                                </highcharts-chart>
                            </div>

                            <!-- Chart 5: Weekly Pattern -->
                            <div class="chart-container">
                                <highcharts-chart
                                    [callbackFunction]="chartCallback"
                                    [Highcharts]="Highcharts"
                                    [options]="weeklyPatternChart">
                                </highcharts-chart>
                            </div>
                        </div>
                    </div>

                    <div class="document-divider"></div>
                }

                <!-- Configured Events -->
                <div class="document-section">
                    <h2 class="section-heading">CONFIGURED EVENTS</h2>
                    <p class="section-description">The following events are configured in this schedule:</p>

                    @for (event of reportData.events; track event.schedule_event_id) {
                        <div class="event-block">
                            <div class="event-header">
                                <span class="event-number">Event #{{ $index + 1 }}</span>
                                <span
                                    class="event-status">{{ event.enabled !== '0' ? '[ACTIVE]' : '[INACTIVE]' }}</span>
                            </div>
                            <table class="event-details-table">
                                <tr>
                                    <td class="detail-label">Name:</td>
                                    <td class="detail-value">{{ getEventName(event) }}</td>
                                </tr>
                                <tr>
                                    <td class="detail-label">Type:</td>
                                    <td class="detail-value">{{ getEventType(event) | uppercase }}</td>
                                </tr>
                                <tr>
                                    <td class="detail-label">Start Time:</td>
                                    <td class="detail-value">{{ schedulerReportService.formatTime(event.time) }}</td>
                                </tr>
                                <tr>
                                    <td class="detail-label">End Time:</td>
                                    <td class="detail-value">{{ getEventEndTime(event) }}</td>
                                </tr>
                                <tr>
                                    <td class="detail-label">Duration:</td>
                                    <td class="detail-value">{{ schedulerReportService.formatDuration(getEventDuration(event)) }}</td>
                                </tr>
                                <tr>
                                    <td class="detail-label">Recurrence:</td>
                                    <td class="detail-value">{{ getRecurrenceDescription(event) | uppercase }}</td>
                                </tr>
                                @if (event.priority) {
                                    <tr>
                                        <td class="detail-label">Priority:</td>
                                        <td class="detail-value">{{ event.priority }}</td>
                                    </tr>
                                }
                                @if (event.start_date) {
                                    <tr>
                                        <td class="detail-label">Start Date:</td>
                                        <td class="detail-value">{{ formatDate(event.start_date) }}</td>
                                    </tr>
                                }
                                @if (event.end_date) {
                                    <tr>
                                        <td class="detail-label">End Date:</td>
                                        <td class="detail-value">{{ formatDate(event.end_date) }}</td>
                                    </tr>
                                }
                            </table>
                        </div>
                    }
                </div>

                <div class="document-divider"></div>

                <!-- Scheduled Occurrences (Main Focus) -->
                <div class="document-section">
                    <h2 class="section-heading">SCHEDULED CONTENT - {{ getPeriodDescription() }}</h2>
                    <p class="section-description">
                        Based on the current configuration, the following content is scheduled to run:
                    </p>

                    @if (getVisibleOccurrences().length > 0) {
                        <table class="occurrences-table">
                            <thead>
                            <tr>
                                <th class="col-num">#</th>
                                <th class="col-date">Date</th>
                                <th class="col-day">Day</th>
                                <th class="col-time">Time</th>
                                <th class="col-duration">Duration</th>
                                <th class="col-content">Content</th>
                                <th class="col-type">Type</th>
                            </tr>
                            </thead>
                            <tbody>
                                @for (occurrence of getVisibleOccurrences(); track $index) {
                                    <tr [class.date-change]="$index === 0 || occurrence.date !== getVisibleOccurrences()[$index - 1].date">
                                        <td class="col-num">{{ $index + 1 }}</td>
                                        <td class="col-date">{{ occurrence.date }}</td>
                                        <td class="col-day">{{ occurrence.dayOfWeek }}</td>
                                        <td class="col-time">{{ occurrence.time }}</td>
                                        <td class="col-duration">{{ schedulerReportService.formatDuration(occurrence.effectiveDuration || getEventDuration(occurrence.event)) }}</td>
                                        <td class="col-content">{{ getEventName(occurrence.event) }}</td>
                                        <td class="col-type">{{ getEventType(occurrence.event) | uppercase }}</td>
                                    </tr>
                                }
                            </tbody>
                            <tfoot>
                            <tr class="total-row">
                                <td colspan="4" class="total-label">Total:</td>
                                <td class="total-value">{{ schedulerReportService.formatDuration(getTotalEffectiveDuration()) }}</td>
                                <td colspan="2"></td>
                            </tr>
                            </tfoot>
                        </table>

                        <div class="table-footer">
                            Total scheduled occurrences: {{ getVisibleOccurrences().length }}
                        </div>
                    } @else {
                        <p class="no-data">No scheduled content found for the specified period.</p>
                    }
                </div>

                <!-- Conflicts Warning -->
                @if (reportData.conflicts.length > 0) {
                    <div class="document-divider"></div>
                    <div class="document-section">
                        <h2 class="section-heading">⚠ SCHEDULING CONFLICTS DETECTED</h2>
                        <p class="section-description warning-text">
                            Multiple events are scheduled to run at the same time. This may cause unexpected behavior.
                        </p>

                        <!-- Conflict Summary -->
                        <div class="conflict-summary">
                            <h3 class="summary-heading">CONFLICT OVERVIEW</h3>
                            <table class="summary-table">
                                <tr>
                                    <td class="summary-label">Total Conflicts:</td>
                                    <td class="summary-value warning-value">{{ reportData.conflictSummary.totalConflicts }}</td>
                                </tr>
                                <tr>
                                    <td class="summary-label">Affected Dates:</td>
                                    <td class="summary-value">{{ reportData.conflictSummary.affectedDates }}</td>
                                </tr>
                            </table>

                            <!-- Top Conflict Dates -->
                            @if (reportData.conflictSummary.topConflictDates.length > 0) {
                                <h4 class="subsection-heading">Dates with Most Conflicts</h4>
                                <table class="conflict-table">
                                    <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Day</th>
                                        <th>Conflicts</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                        @for (item of reportData.conflictSummary.topConflictDates; track item.date) {
                                            <tr>
                                                <td>{{ item.date }}</td>
                                                <td>{{ getDayOfWeek(item.date) }}</td>
                                                <td class="warning-value">{{ item.count }}</td>
                                            </tr>
                                        }
                                    </tbody>
                                </table>
                            }

                            <!-- Top Conflict Time Slots -->
                            @if (reportData.conflictSummary.conflictsByTimeSlot.length > 0) {
                                <h4 class="subsection-heading">Time Slots with Most Conflicts</h4>
                                <table class="conflict-table">
                                    <thead>
                                    <tr>
                                        <th>Time</th>
                                        <th>Conflicts</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                        @for (item of reportData.conflictSummary.conflictsByTimeSlot; track item.time) {
                                            <tr>
                                                <td>{{ item.time }}</td>
                                                <td class="warning-value">{{ item.count }}</td>
                                            </tr>
                                        }
                                    </tbody>
                                </table>
                            }
                        </div>

                        <!-- Detailed Conflicts (show first 15) -->
                        <h3 class="summary-heading">DETAILED CONFLICTS @if (reportData.conflicts.length > 15) {
                            (Showing first 15 of {{ reportData.conflicts.length }})
                        }</h3>
                        <table class="occurrences-table conflict-details-table">
                            <thead>
                            <tr>
                                <th class="col-num">#</th>
                                <th class="col-date">Date</th>
                                <th class="col-time">Time</th>
                                <th class="col-content">Conflicting Events</th>
                                <th class="col-type">Types</th>
                                <th class="col-duration">Durations</th>
                            </tr>
                            </thead>
                            <tbody>
                                @for (conflict of reportData.conflicts.slice(0, 15); track $index) {
                                    <tr>
                                        <td class="col-num">{{ $index + 1 }}</td>
                                        <td class="col-date">{{ conflict.date }}</td>
                                        <td class="col-time">{{ conflict.time }}</td>
                                        <td class="col-content">
                                            @for (event of conflict.events; track event.schedule_event_id) {
                                                <div class="conflict-event-item">• {{ getEventName(event) }}</div>
                                            }
                                        </td>
                                        <td class="col-type">
                                            @for (event of conflict.events; track event.schedule_event_id) {
                                                <div class="conflict-event-item">{{ getEventType(event) | uppercase }}
                                                </div>
                                            }
                                        </td>
                                        <td class="col-duration">
                                            @for (event of conflict.events; track event.schedule_event_id) {
                                                <div
                                                    class="conflict-event-item">{{ schedulerReportService.formatDuration(getEventDuration(event)) }}
                                                </div>
                                            }
                                        </td>
                                    </tr>
                                }
                            </tbody>
                        </table>
                    </div>
                }

                <div class="document-divider"></div>

                <!-- Content Inventory -->
                <div class="document-section">
                    <h2 class="section-heading">CONTENT INVENTORY</h2>
                    <p class="section-description">Summary of all content referenced in this schedule:</p>

                    @if (reportData.resourceInventory.length > 0) {
                        <table class="inventory-table">
                            <thead>
                            <tr>
                                <th class="col-num">#</th>
                                <th class="col-type">Type</th>
                                <th class="col-name">Name</th>
                                <th class="col-usage">Usage Count</th>
                            </tr>
                            </thead>
                            <tbody>
                                @for (item of reportData.resourceInventory; track item.id) {
                                    <tr>
                                        <td class="col-num">{{ $index + 1 }}</td>
                                        <td class="col-type">{{ item.type | uppercase }}</td>
                                        <td class="col-name">{{ item.name }}</td>
                                        <td class="col-usage">{{ item.usageCount }}</td>
                                    </tr>
                                }
                            </tbody>
                        </table>
                    } @else {
                        <p class="no-data">No content items found.</p>
                    }
                </div>

                <div class="document-divider"></div>

                <!-- Recurrence Summary -->
                <div class="document-section">
                    <h2 class="section-heading">RECURRENCE PATTERN SUMMARY</h2>
                    <table class="summary-table">
                        <tr>
                            <td class="summary-label">One-Time Events:</td>
                            <td class="summary-value">{{ reportData.recurrenceBreakdown.once.length }}</td>
                        </tr>
                        <tr>
                            <td class="summary-label">Daily Events:</td>
                            <td class="summary-value">{{ reportData.recurrenceBreakdown.daily.length }}</td>
                        </tr>
                        <tr>
                            <td class="summary-label">Weekly Events:</td>
                            <td class="summary-value">{{ reportData.recurrenceBreakdown.weekly.length }}</td>
                        </tr>
                        <tr>
                            <td class="summary-label">Monthly Events:</td>
                            <td class="summary-value">{{ reportData.recurrenceBreakdown.monthly.length }}</td>
                        </tr>
                        <tr>
                            <td class="summary-label">Yearly Events:</td>
                            <td class="summary-value">{{ reportData.recurrenceBreakdown.yearly.length }}</td>
                        </tr>
                        <tr>
                            <td class="summary-label">Hourly Events:</td>
                            <td class="summary-value">{{ reportData.recurrenceBreakdown.hourly.length }}</td>
                        </tr>
                    </table>
                </div>

                <!-- Document Footer -->
                <div class="document-footer">
                    <div class="footer-line"></div>
                    <p class="footer-text">End of Report</p>
                    <p class="footer-note">This report is generated based on the current schedule configuration and
                        forecasts content execution for {{ getForecastPeriod().toLowerCase() }}.</p>
                </div>
            </div>
        }
    </div>

    <!-- Footer Actions -->
    <div mat-dialog-actions class="dialog-actions no-print">
        <button mat-button (click)="copyToClipboard()" [disabled]="loading">
            <mat-icon>content_copy</mat-icon>
            {{ 'ai.copyText' | transloco }}
        </button>
        <button mat-button (click)="printReport()" [disabled]="loading">
            <mat-icon>print</mat-icon>
            {{ 'ai.print' | transloco }}
        </button>
        <div class="action-spacer"></div>
        <button mat-button (click)="close()">
            {{ 'general.close' | transloco }}
        </button>
    </div>
</div>
